import { MessageInstance } from 'antd/es/message/interface'
import { AxiosResponse } from 'axios'
import { AssetDataObjType } from 'features/assetDatabase/types/assetOverviewTypes'
import {
  GeckoComponentCoords,
  GeckoEntry,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoReport,
  type GeckoUnit,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'
import { v4 } from 'uuid'
export const generateGeometricPinQuery = (
  pendingDeleteIndex: number | null,
  selectedImageIndex: number | null,
  subSectionThumbnails: readonly GeckoReport[]
) => {
  // The index can be derived from a pendingDeleteIndex or selectedImageIndex
  const choosenThumbnailIndex = pendingDeleteIndex ?? selectedImageIndex ?? null
  const thumbnailId =
    choosenThumbnailIndex !== null
      ? subSectionThumbnails[choosenThumbnailIndex]?.id
      : undefined

  return thumbnailId
    ? {
        type: RolodexEntryType.GEOMETRIC_PIN,
        tags: { report: [thumbnailId] },
      }
    : undefined
}

export const getInspection = (
  componentID: string | undefined,
  inspectionID: string | undefined,
  inspections: readonly GeckoInspection[]
) => {
  // if there is an inspectionID, return the inspection
  // if there is a componentID, return the first inspection that has that componentID in its tags
  if (inspectionID) {
    return inspections.find((inspection) => inspection.id === inspectionID)
  }
  if (componentID) {
    return inspections.find(
      (inspection) => inspection.tags?.component === componentID
    )
  }
}

export const getComponentId = (
  inspections: readonly GeckoInspection[],
  componentID: string | undefined,
  inspectionID: string | undefined
) => {
  if (componentID) {
    return componentID
  }
  if (inspectionID) {
    return inspections.find((inspection) => inspection.id === inspectionID)
      ?.tags?.component
  }
}

export const getComponentInspections = (
  componentID: string | undefined,
  inspections: readonly GeckoInspection[]
) => {
  return inspections
    .filter((inspection) => inspection.tags?.component === componentID)
    .sort((a, b) => b.data.date.getTime() - a.data.date.getTime())
}

export const convertCoordstoLocalUnits = (
  coords: GeckoComponentCoords,
  getPreferredMeasurementUnit: (
    unit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit
) => {
  const xUnit = getPreferredMeasurementUnit(coords.x.unit)
  const yUnit = getPreferredMeasurementUnit(coords.y.unit)

  return {
    x: coords.x.convertTo(xUnit),
    y: coords.y.convertTo(yUnit),
  }
}

export const getInspectionTags = (
  inspections: readonly GeckoInspection[],
  inspectionID: string
) => {
  return inspections.find((inspection) => inspection.id === inspectionID)?.tags
}

export const createGeometricPinTags = (
  componentID: string | undefined,
  inspectionID: string | undefined,
  unit: GeckoUnit | undefined,
  imageThumbnail: GeckoReport,
  inspections: readonly GeckoInspection[]
) => {
  if (componentID) {
    return {
      component: componentID,
      site: imageThumbnail.tags?.site,
      organization: imageThumbnail.tags?.organization,
      unit: unit?.id,
      report: imageThumbnail.id,
    }
  }
  if (inspectionID) {
    const inspectionTags = getInspectionTags(inspections, inspectionID)
    return {
      inspection: inspectionID,
      component: inspectionTags?.component,
      site: inspectionTags?.site,
      organization: inspectionTags?.organization,
      unit: unit?.id,
      report: imageThumbnail.id,
    }
  }
  return {}
}

export const getComponentModelType = (
  inspections: readonly GeckoInspection[],
  componentID: string | undefined,
  inspectionID: string | undefined,
  assetDataObj: AssetDataObjType | undefined
) => {
  let determinedComponentId
  if (componentID) {
    determinedComponentId = componentID
  }
  if (inspectionID) {
    determinedComponentId = inspections.find(
      (inspection) => inspection.id === inspectionID
    )?.tags?.component
  }
  if (determinedComponentId && assetDataObj) {
    return assetDataObj.components.find(
      (component) => component.componentData.uniqueID === determinedComponentId
    )?.componentData.componentJSON
  }
  return undefined
}

interface UpdateImageThumbnailParams {
  id: string
  fileName: string
  fileCaption: string
  rolodexPatch: (
    entries: RolodexEntryPatchJson[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<AxiosResponse<any, any>>
  messageApi: MessageInstance
}

export const updateImageThumbnail = async ({
  id,
  fileName,
  fileCaption,
  rolodexPatch,
  messageApi,
}: UpdateImageThumbnailParams): Promise<boolean> => {
  try {
    await rolodexPatch([
      {
        id,
        data: {
          visible_name: fileName,
          file_caption: fileCaption,
        },
      },
    ])
    messageApi.success('Image updated successfully')
    return true
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error'
    messageApi.error(`Error updating image metadata: ${errorMessage}`)
    return false
  }
}

interface UpdateGeometricPinParams {
  x: { num: number; unit: { name: string } }
  y: { num: number; unit: { name: string } }
  geometricPins: readonly GeckoEntry[]
  componentID?: string
  inspectionID?: string
  unit?: GeckoUnit
  imageThumbnail: GeckoReport
  inspections: readonly GeckoInspection[]
  rolodexPatch: (
    entries: RolodexEntryPatchJson[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<AxiosResponse<any, any>>
  messageApi: MessageInstance
}

export const updateGeometricPin = async ({
  x,
  y,
  geometricPins,
  componentID,
  inspectionID,
  unit,
  imageThumbnail,
  inspections,
  rolodexPatch,
  messageApi,
}: UpdateGeometricPinParams): Promise<void> => {
  try {
    const patchBodyData = {
      x: x.num,
      y: y.num,
      x_measurement_unit: x.unit.name,
      y_measurement_unit: y.unit.name,
    }

    if (geometricPins.length > 0) {
      await rolodexPatch([
        {
          id: geometricPins[0].id,
          data: patchBodyData as unknown as Record<string, unknown>,
        },
      ])
    } else {
      const tags = createGeometricPinTags(
        componentID,
        inspectionID,
        unit,
        imageThumbnail,
        inspections
      )

      await rolodexPatch([
        {
          id: v4(),
          type: 'geometric-pin',
          data: patchBodyData as unknown as Record<string, unknown>,
          tags,
        } as RolodexEntryPatchJson,
      ])
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error'
    messageApi.error(`Error updating coordinates: ${errorMessage}`)
  }
}

export const handleEnterToBlur = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    e.currentTarget.blur()
  }
}
